import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { Badge, ConfirmModal, Loader, useToast } from '@lms/ui';
import { SearchInput } from '@/components/SearchInput';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { usePermissions } from '@/hooks/usePermissions';
import { api, listResource } from '@/lib/api';
import { credentialSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';

interface Credential {
  id: string;
  title: string;
  recipientName?: string;
  recipient?: { name: string };
  verificationCode?: string;
  status?: string;
  issuedAt?: string;
  user?: { avatarUrl?: string };
}

const STATUS_LABELS: Record<string, string> = {
  issued: 'Emitida',
  revoked: 'Revocada',
  expired: 'Expirada',
};

const STATUS_TABS = [
  { value: '', label: 'Todos' },
  { value: 'issued', label: 'Emitida' },
  { value: 'revoked', label: 'Revocada' },
  { value: 'expired', label: 'Expirada' },
];

function initials(name: string) {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? parts[0]?.[1] ?? '')).toUpperCase();
}

export function CredencialesPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.CREDENCIALES_ADMINISTRAR);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const appliedSearch = useDebouncedValue(search, 300).trim();
  const [deleteTarget, setDeleteTarget] = useState<Credential | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/credentials/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setDeleteTarget(null);
      await queryClient.invalidateQueries({ queryKey: ['credentials'] });
      toast(credentialSuccessToast('delete'));
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar la credencial'));
    },
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['credentials', appliedSearch],
    queryFn: () =>
      listResource<Credential>('/credentials', {
        perPage: 50,
        ...(appliedSearch ? { search: appliedSearch } : {}),
      }),
  });

  const credentials = useMemo(() => {
    const rows = data?.data ?? [];
    if (!status) return rows;
    return rows.filter((credential) => credential.status === status);
  }, [data?.data, status]);

  return (
    <div className="lms-admin-credentials lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-4">
          <HubBackLink to="/administracion?tab=general" />
          <div>
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Credenciales emitidas</h1>
            <p className="mt-1 text-dash-on-surface-variant">Historial y validaciones de certificados académicos</p>
          </div>
        </div>
        <Link
          to="/credenciales/emitir"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-dash-primary px-6 py-3 text-sm font-medium text-white shadow-sm transition-opacity hover:opacity-90"
        >
          <span className="material-symbols-outlined text-[1.25rem]" aria-hidden="true">
            verified
          </span>
          Emitir manualmente
        </Link>
      </div>

      <div className="lms-credentials-glass-panel mb-8 overflow-hidden rounded-xl shadow-sm">
        <form
          method="get"
          action="/credenciales"
          className="flex flex-col justify-between gap-4 p-6 lg:flex-row lg:items-center"
          onSubmit={(event) => event.preventDefault()}
        >
          <input type="hidden" name="status" value={status} />
          <div className="flex flex-grow flex-wrap items-center gap-4">
            <SearchInput
              name="search"
              value={search}
              onChange={setSearch}
              placeholder="Buscar"
              aria-label="Buscar credencial"
              className="min-w-[17.5rem] flex-1"
            />

            <div className="flex items-center gap-3 rounded-lg border border-dash-outline-variant bg-secondary-100 p-1">
              <span className="px-3 text-xs font-semibold uppercase tracking-wider text-dash-on-surface-variant">Estado</span>
              <div className="flex flex-wrap gap-1">
                {STATUS_TABS.map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => setStatus(tab.value)}
                    className={`rounded-md px-4 py-1.5 text-sm transition-colors ${
                      status === tab.value
                        ? 'border border-dash-outline-variant bg-white text-dash-on-surface shadow-sm'
                        : 'text-dash-on-surface-variant hover:bg-white/50'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </form>

      </div>

      {error && (
        <p className="mb-4 text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      <section className="lms-gradebook lms-gradebook--forum">
        <div className="lms-course-forum-card">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : credentials.length === 0 ? (
            <div className="lms-gradebook__table-wrap">
              <div className="lms-gradebook__empty">
                <div className="lms-gradebook__empty-icon" aria-hidden="true">
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                    workspace_premium
                  </span>
                </div>
                <h3>Sin credenciales</h3>
                <p>No hay credenciales que coincidan con los filtros.</p>
              </div>
            </div>
          ) : (
            <>
              <div className="lms-gradebook__table-wrap">
                <table className="lms-gradebook__table">
                  <thead>
                    <tr>
                      <th className="lms-gradebook__th" scope="col">
                        Estudiante
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Título
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Código
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Estado
                      </th>
                      <th className="lms-gradebook__th" scope="col">
                        Emitida
                      </th>
                      <th className="lms-gradebook__th lms-gradebook__th--actions" scope="col">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {credentials.map((credential) => {
                      const name = credential.recipientName ?? credential.recipient?.name ?? '—';
                      const st = credential.status ?? 'issued';

                      return (
                        <tr key={credential.id}>
                          <th className="lms-gradebook__td" scope="row">
                            <div className="lms-gradebook__student">
                              {credential.user?.avatarUrl ? (
                                <img src={credential.user.avatarUrl} alt="" className="lms-gradebook__avatar" />
                              ) : (
                                <div className="lms-gradebook__avatar" aria-hidden="true">
                                  {initials(name) || '?'}
                                </div>
                              )}
                              <Link to={`/credenciales/${credential.id}`} className="lms-gradebook__name">
                                {name}
                              </Link>
                            </div>
                          </th>
                          <td className="lms-gradebook__td">
                            <span className="lms-gradebook__muted">{credential.title}</span>
                          </td>
                          <td className="lms-gradebook__td">
                            <span className="lms-gradebook__muted">{credential.verificationCode ?? '—'}</span>
                          </td>
                          <td className="lms-gradebook__td">
                            <Badge
                              variant={st === 'revoked' ? 'danger' : st === 'expired' ? 'warning' : st === 'issued' ? 'success' : 'default'}
                            >
                              {STATUS_LABELS[st] ?? st}
                            </Badge>
                          </td>
                          <td className="lms-gradebook__td">
                            <span className="lms-gradebook__muted">
                              {credential.issuedAt ? new Date(credential.issuedAt).toLocaleDateString('es') : '—'}
                            </span>
                          </td>
                          <td className="lms-gradebook__td lms-gradebook__td--actions">
                            <div className="flex items-center justify-end gap-1">
                              <Link
                                to={`/credenciales/${credential.id}`}
                                className="lms-gradebook__action"
                                title="Ver"
                                aria-label={`Ver credencial de ${name}`}
                              >
                                <span className="material-symbols-outlined" aria-hidden="true">
                                  visibility
                                </span>
                              </Link>
                              {canManage ? (
                                <Link
                                  to={`/credenciales/${credential.id}/edit`}
                                  className="lms-gradebook__action"
                                  title="Editar"
                                  aria-label={`Editar credencial de ${name}`}
                                >
                                  <span className="material-symbols-outlined" aria-hidden="true">
                                    edit
                                  </span>
                                </Link>
                              ) : null}
                              {canManage ? (
                                <button
                                  type="button"
                                  className="lms-gradebook__action"
                                  title="Eliminar"
                                  aria-label={`Eliminar credencial de ${name}`}
                                  onClick={() => setDeleteTarget(credential)}
                                >
                                  <span className="material-symbols-outlined" aria-hidden="true">
                                    delete
                                  </span>
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="lms-course-forum-card__footer">
                <span className="lms-course-forum-card__meta">
                  Mostrando <strong>1</strong> a <strong>{credentials.length}</strong> de{' '}
                  <strong>{credentials.length}</strong> resultados
                </span>
                <div className="lms-course-forum-card__pager">
                  <button type="button" className="lms-course-forum-card__pager-btn" aria-label="Página anterior" disabled>
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chevron_left
                    </span>
                  </button>
                  <span className="lms-course-forum-card__pager-current">1</span>
                  <button type="button" className="lms-course-forum-card__pager-btn" aria-label="Página siguiente" disabled>
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chevron_right
                    </span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        title="Eliminar credencial"
        description={
          deleteTarget
            ? `¿Eliminar la credencial «${deleteTarget.title}» de ${deleteTarget.recipientName ?? deleteTarget.recipient?.name ?? 'este estudiante'}? Esta acción no se puede deshacer.`
            : undefined
        }
        confirmLabel="Eliminar"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
}
